/**
 * /reset-password?t=<token>&scope=student|teacher
 *
 * The reset link emailed to a user lands here. We POST to the matching scoped
 * endpoint and redirect to the right sign-in page afterwards.
 */
import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("t") || "";
  const scope = params.get("scope") === "teacher" ? "teacher" : "student";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const loginPath = scope === "teacher" ? "/teachers" : "/login";
  const hasToken = useMemo(() => token.length >= 8, [token]);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (pw.length < 8) {
      setError("Choose a password of at least 8 characters.");
      return;
    }
    if (pw !== pw2) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const call = scope === "teacher" ? api.teacherResetPassword : api.studentResetPassword;
      await call(token, pw);
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate(loginPath, { replace: true }), 1800);
    } catch (err) {
      setError(err.message || "Could not reset your password.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <main
      className="min-h-[100dvh] bg-[#FAF9F6] pt-24 md:pt-32 pb-12 px-6"
      data-testid="reset-password-page"
    >
      <div className="max-w-md mx-auto">
        <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
          {scope === "teacher" ? "Teachers" : "Students"}
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          Choose a new password.
        </h1>

        {!hasToken ? (
          <div className="mt-8 bg-white border border-[#1C1C1C]/10 rounded-2xl p-8" data-testid="reset-password-invalid-link">
            <p className="text-[#1C1C1C]/80 leading-relaxed">
              This reset link is incomplete or invalid. Please request a new one.
            </p>
            <Link
              to={scope === "teacher" ? "/teachers/forgot" : "/forgot-password"}
              className="mt-6 inline-block px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-xs tracking-[0.18em] uppercase font-medium hover:bg-[#1C1C1C]/85"
            >
              Request a new link →
            </Link>
          </div>
        ) : done ? (
          <div className="mt-8 bg-white border border-[#1C1C1C]/10 rounded-2xl p-8" data-testid="reset-password-done">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-[10px] tracking-[0.22em] uppercase font-medium mb-4">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              Password updated
            </div>
            <p className="text-[#1C1C1C]/80 leading-relaxed">
              You're all set. Redirecting you to sign in…
            </p>
          </div>
        ) : (
          <>
            <p className="mt-4 text-base text-[#1C1C1C]/70 leading-relaxed">
              Pick something at least 8 characters long. This link can be used only once.
            </p>
            <form onSubmit={onSubmit} className="mt-10 space-y-4">
              <Field label="New password">
                <input
                  data-testid="reset-password-new"
                  autoFocus
                  required
                  type="password"
                  minLength={8}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="input"
                />
              </Field>
              <Field label="Confirm new password">
                <input
                  data-testid="reset-password-confirm"
                  required
                  type="password"
                  minLength={8}
                  autoComplete="new-password"
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  className="input"
                />
              </Field>
              {error && <div className="text-sm text-[#B83A3A]" data-testid="reset-password-error">{error}</div>}
              <button
                data-testid="reset-password-submit"
                type="submit"
                disabled={loading}
                className="w-full px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
              >
                {loading ? "Updating…" : "Update password"}
              </button>
              <div className="text-center">
                <Link
                  to={loginPath}
                  className="text-[11px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 hover:text-[#1C1C1C]"
                >
                  ← Back to sign in
                </Link>
              </div>
            </form>
          </>
        )}

        <style>{`.input{width:100%;padding:.75rem 1rem;background:white;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:1rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4)}`}</style>
      </div>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/60 mb-2">{label}</div>
      {children}
    </label>
  );
}
